"use server";

import { revalidatePath } from "next/cache";
import { redirect } from "next/navigation";

import { getTranslations } from "next-intl/server";
import { z } from "zod";

import { UI_LOCALES } from "@/config/locales";
import { routes } from "@/config/routes";
import {
  acceptInvitationSchema,
  forgotPasswordSchema,
  loginResponseSchema,
  loginSchema,
  mfaVerifySchema,
  sessionUserSchema,
  resetPasswordSchema,
  tokenPairSchema,
  verifyEmailSchema,
} from "@/features/auth/schemas";
import { errorMessage } from "@/i18n/api-errors";
import { fail, ok, type ActionResult, type FieldErrors } from "@/lib/actions";
import { apiFetch } from "@/lib/api/client";
import { ApiError } from "@/lib/api/errors";
import { clearTokenCookies, setTokenCookies } from "@/lib/auth/cookies";
import { safeRedirectPath } from "@/lib/auth/redirect";
import { requireUser } from "@/lib/auth/session";
import { reportError } from "@/lib/logger";

/** "Please fix the highlighted fields", with the field errors (catalog keys) attached. */
async function invalidFields(fieldErrors: FieldErrors): Promise<ActionResult<never>> {
  const t = await getTranslations("common");
  return fail(t("fixFields"), fieldErrors);
}

/**
 * Turns expected backend errors into ActionResult with the translated message for their
 * code (i18n rule 9); rethrows the rest.
 */
async function expectedFailure(
  error: unknown,
  codes: readonly string[] = [],
): Promise<ActionResult<never>> {
  if (error instanceof ApiError) {
    if (codes.includes(error.code)) return fail(await errorMessage(error.code), error.fieldErrors);
    // TOO_MANY_ATTEMPTS (sign-in lockout) or the general RATE_LIMITED
    if (error.status === 429) return fail(await errorMessage(error.code));
    if (error.status === 400 && error.fieldErrors) return invalidFields(error.fieldErrors);
  }
  throw error;
}

// The auth actions below are public by nature: they run without a signed-in user.

/**
 * Password step. Signs in and redirects, or, when the user has MFA, returns the challenge
 * for the code step (membership-and-mfa.md rule 16).
 */
export async function loginAction(
  input: unknown,
  next: unknown,
): Promise<ActionResult<{ challengeToken: string }>> {
  const parsed = loginSchema.safeParse(input);
  if (!parsed.success) return invalidFields(z.flattenError(parsed.error).fieldErrors);

  let result;
  try {
    result = await apiFetch("/auth/login", {
      method: "POST",
      body: parsed.data,
      schema: loginResponseSchema,
      auth: false,
    });
  } catch (error) {
    return expectedFailure(error, ["INVALID_CREDENTIALS"]);
  }

  if (result.challengeToken) return ok({ challengeToken: result.challengeToken });
  if (!result.tokens) throw new Error("Sign-in returned neither tokens nor a challenge");
  await setTokenCookies(result.tokens);
  redirect(safeRedirectPath(next)); // outside try/catch: redirect() works by throwing
}

/**
 * Code step: a TOTP or recovery code for the challenge. A challenge that ended (5 wrong
 * codes, or expired) comes back with a `challengeToken` field error: enter the password again.
 */
export async function verifyMfaAction(input: unknown, next: unknown): Promise<ActionResult<never>> {
  const parsed = mfaVerifySchema.safeParse(input);
  if (!parsed.success) return invalidFields(z.flattenError(parsed.error).fieldErrors);

  let tokens;
  try {
    tokens = await apiFetch("/auth/mfa/verify", {
      method: "POST",
      body: parsed.data,
      schema: tokenPairSchema,
      auth: false,
    });
  } catch (error) {
    if (error instanceof ApiError && error.code === "MFA_CHALLENGE_INVALIDATED") {
      return fail(await errorMessage(error.code), { challengeToken: [error.code] });
    }
    return expectedFailure(error, ["MFA_CODE_INVALID", "MFA_CODE_REUSED"]);
  }

  await setTokenCookies(tokens);
  redirect(safeRedirectPath(next));
}

/** Always "ok" for valid input: the backend never says whether the email exists. */
export async function requestPasswordResetAction(input: unknown): Promise<ActionResult> {
  const parsed = forgotPasswordSchema.safeParse(input);
  if (!parsed.success) return invalidFields(z.flattenError(parsed.error).fieldErrors);

  try {
    await apiFetch("/auth/password-reset/request", {
      method: "POST",
      body: parsed.data,
      schema: z.unknown(),
      auth: false,
    });
  } catch (error) {
    return expectedFailure(error);
  }
  return ok(undefined);
}

export async function resetPasswordAction(input: unknown): Promise<ActionResult<never>> {
  const parsed = resetPasswordSchema.safeParse(input);
  if (!parsed.success) return invalidFields(z.flattenError(parsed.error).fieldErrors);

  try {
    await apiFetch("/auth/password-reset/confirm", {
      method: "POST",
      body: parsed.data,
      schema: z.undefined(),
      auth: false,
    });
  } catch (error) {
    return expectedFailure(error, ["INVALID_RESET_TOKEN"]);
  }

  // The backend signed this user out everywhere; drop any cookies this browser still has.
  await clearTokenCookies();
  redirect("/login?reset=1");
}

/** Sets the invited user's password and signs them in. */
export async function acceptInvitationAction(input: unknown): Promise<ActionResult<never>> {
  const parsed = acceptInvitationSchema.safeParse(input);
  if (!parsed.success) return invalidFields(z.flattenError(parsed.error).fieldErrors);

  let tokens;
  try {
    tokens = await apiFetch("/auth/invitations/accept", {
      method: "POST",
      body: parsed.data,
      schema: tokenPairSchema,
      auth: false,
    });
  } catch (error) {
    return expectedFailure(error, ["INVITATION_INVALID", "INVITATION_EXPIRED"]);
  }

  // Replaces whatever session this browser had: the invitation is for this person
  await setTokenCookies(tokens);
  redirect(routes.dashboard());
}

export async function verifyEmailAction(input: unknown): Promise<ActionResult> {
  const parsed = verifyEmailSchema.safeParse(input);
  if (!parsed.success) {
    const t = await getTranslations("auth.verify");
    return fail(t("incompleteLink"));
  }

  try {
    await apiFetch("/auth/email-verification/confirm", {
      method: "POST",
      body: parsed.data,
      schema: z.undefined(),
      auth: false,
    });
  } catch (error) {
    return expectedFailure(error, ["INVALID_VERIFICATION_TOKEN"]);
  }
  return ok(undefined);
}

/** Emails a new verification link to the signed-in user. */
export async function resendVerificationAction(): Promise<ActionResult> {
  await requireUser();
  try {
    await apiFetch("/auth/email-verification/resend", { method: "POST", schema: z.unknown() });
  } catch (error) {
    return expectedFailure(error);
  }
  return ok(undefined);
}

export async function logoutAction(): Promise<void> {
  await endSession("/auth/logout");
}

/** Ends every session of this user, on every device. */
export async function logoutAllAction(): Promise<void> {
  await endSession("/auth/logout-all");
}

async function endSession(path: "/auth/logout" | "/auth/logout-all"): Promise<void> {
  try {
    await apiFetch(path, { method: "POST", schema: z.undefined() });
  } catch (error) {
    // The session may already be gone; signing out locally must still work.
    if (!(error instanceof ApiError && error.status === 401)) reportError(error);
  }
  await clearTokenCookies();
  redirect("/login");
}

/** The signed-in user's language (PATCH /v1/me). The whole app re-renders in it. */
export async function updateLocaleAction(locale: unknown): Promise<ActionResult> {
  await requireUser();
  const parsed = z.enum(UI_LOCALES).safeParse(locale);
  if (!parsed.success) return fail(await errorMessage("VALIDATION_FAILED"));

  try {
    await apiFetch("/me", {
      method: "PATCH",
      body: { locale: parsed.data },
      schema: sessionUserSchema,
    });
  } catch (error) {
    return expectedFailure(error);
  }
  // <html lang>, the catalogs and every page come from the root layout
  revalidatePath("/", "layout");
  return ok(undefined);
}
